import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useNavLinksContext, NavLinksSet } from '../../contexts/NavLinksContext';
import { useAppContext } from '../../contexts/Storage';
import dashboard from '../../assets/images/dashboard_icon.png';
import greyDashboard from '../../assets/images/dashboard_icon_grey.png';
import records from '../../assets/images/record_icon.png';
import greyRecords from '../../assets/images/record_icon_grey.png';

// Icon mapping for known routes
const iconMap: Record<string, { active: string; inactive: string }> = {
  '/dashboard': {
    active: dashboard,
    inactive: greyDashboard,
  },
  '/records': {
    active: records,
    inactive: greyRecords,
  },
  // Add more icon mappings as needed
};

// Record sublinks configuration
const RECORD_SUBLINKS = [
  { to: '/records/transactions', label: 'Transactions' },
  { to: '/records/transaction-entities', label: 'Transaction Entities' },
  { to: '/records/accounts', label: 'Accounts' },
];

interface NavLinksProps {
  customLinks?: NavLinksSet;
}

const NavLinks: React.FC<NavLinksProps> = ({ customLinks }) => {
  const location = useLocation();
  const { currentNavLinks } = useNavLinksContext();
  const { selectedRecord } = useAppContext();
  
  const navLinks = customLinks || currentNavLinks;
  
  // Check if we're on a record-related page
  const isRecordPage = location.pathname.startsWith('/records');
  
  // Show sublinks only when a record is selected (global state)
  const showRecordSublinks = !!selectedRecord;
  
  // Check if current path matches a record sublink
  const isActiveRecordSublink = (path: string) => {
    return location.pathname === path;
  };

  const isActive = (path: string) => {
    // For dashboard, exact match
    if (path === '/dashboard') {
      return location.pathname === path;
    }
    // For records, check if we're on any record page
    if (path === '/records') {
      return location.pathname.startsWith('/records');
    }
    return location.pathname === path;
  };

  return (
    <div className="space-y-2">
      {navLinks.title && (
        <h3 className="px-4 py-2 text-sm font-medium text-dark uppercase tracking-wider">
          {navLinks.title}
        </h3>
      )}
      
      {navLinks.links.map((link) => {
        const active = isActive(link.to);
        const icons = iconMap[link.to] || {};
        
        // Special handling for Records link with sublinks
        if (link.to === '/records') {
          return (
            <div key={link.to} className="space-y-1" data-tour="nav-records">
              {/* Main Records link */}
              <Link
                to={link.to}
                className={`px-4 ps-6 py-2 rounded-lg text-dark transition-colors hover:bg-grey_light duration-200 justify-between w-full flex items-center ${
                  active ? 'rounded-none border-s border-primary font-medium' : ''
                }`}
              >
                <h3 className="w-full">{link.label}</h3>
                
                <div className="flex items-center">
                  {icons.active && icons.inactive ? (
                    <img
                      className="ms-2 object-cover"
                      src={active ? icons.active : icons.inactive}
                      alt={`${link.label} icon`}
                      style={{ width: '18px', height: '18px' }}
                    />
                  ) : null}
                </div>
              </Link>
              
              {/* Record sublinks - only shown when a record is selected */}
              {showRecordSublinks && (
                <div className="ml-8 space-y-1">
                  {RECORD_SUBLINKS.map((sublink) => {
                    const sublinkActive = isActiveRecordSublink(sublink.to);
                    const tourId =
                      sublink.to === '/records/transactions'
                        ? 'nav-transactions'
                        : sublink.to === '/records/accounts'
                          ? 'nav-accounts'
                          : sublink.to === '/records/transaction-entities'
                            ? 'nav-entities'
                            : undefined;
                    return (
                      <Link
                        key={sublink.to}
                        to={sublink.to}
                        data-tour={tourId}
                        className={`px-4 py-2 rounded-lg text-dark transition-colors hover:bg-grey_light duration-200 block ${
                          sublinkActive ? 'rounded-none border-s border-primary font-medium' : ''
                        }`}
                      >
                        <span className="text-sm">{sublink.label}</span>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          );
        }
        
        // Regular link (Dashboard)
        return (
          <Link
            key={link.to}
            to={link.to}
            data-tour={link.to === '/dashboard' ? 'nav-dashboard' : undefined}
            className={`px-4 ps-6 py-2 rounded-lg text-dark transition-colors hover:bg-grey_light duration-200 justify-between w-full flex items-center ${
              active ? 'rounded-none border-s border-primary font-medium' : ''
            }`}
          >
            <h3 className="w-full">{link.label}</h3>
            
            {link.icon ? (
              <img
                className="ms-2 object-cover"
                src={link.icon}
                alt={`${link.label} icon`}
                style={{ width: '18px', height: '18px' }}
              />
            ) : icons.active && icons.inactive ? (
              <img
                className="ms-2 object-cover"
                src={active ? icons.active : icons.inactive}
                alt={`${link.label} icon`}
                style={{ width: '18px', height: '18px' }}
              />
            ) : null}
          </Link>
        );
      })}
    </div>
  );
};

export default NavLinks;